import { useState } from 'react'
import { Plus } from 'lucide-react'
import {
  CurrencyValue, DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useComplianceMutations, useGpssaFilings, useWpsSubmissions } from '../api'

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'success' | 'danger' | 'warning'> = {
  PREPARED: 'neutral',
  SUBMITTED: 'info',
  ACCEPTED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'warning',
}

interface Draft {
  id?: string
  legalEntityId: string
  periodStart: string
  periodEnd: string
  reference: string
  status: string
  responseNote: string
  a: string
  b: string
  c: string
}

const EMPTY: Draft = {
  legalEntityId: '', periodStart: '', periodEnd: '', reference: '', status: 'PREPARED',
  responseNote: '', a: '', b: '', c: '',
}

/** What was filed, when, and what came back. */
export function FilingLogsPage() {
  useBreadcrumb()
  const [tab, setTab] = useState<'wps' | 'gpssa'>('wps')
  const wps = useWpsSubmissions()
  const gpssa = useGpssaFilings()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const actions = useComplianceMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)

  const entityName = (id: string) => entities.data?.find((row) => row.id === id)?.name ?? '—'

  return (
    <div className="space-y-4">
      <PageHeader
        title="Filings"
        subtitle="What was sent, and what came back. Recorded, never inferred."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ ...EMPTY, legalEntityId: entities.data?.[0]?.id ?? '' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            {tab === 'wps' ? 'Record a submission' : 'Record a filing'}
          </Button>
        }
      />

      <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        The amounts here are as <strong>filed</strong>, not as this system would compute them
        today. When the two differ, that difference is the finding — recomputing the figure
        would hide it.
      </p>

      <nav className="flex flex-wrap gap-2" aria-label="Filing logs">
        {([['wps', 'Wage protection'], ['gpssa', 'Pension']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? 'page' : undefined}
            className={`rounded-md px-3 py-1.5 text-sm ${tab === key
              ? 'bg-[var(--color-primary)] text-white'
              : 'bg-[var(--color-page)] text-[var(--color-body)]'}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>

      <Card>
        {tab === 'wps' && (
          <>
            {wps.isPending && <Skeleton className="m-4 h-20" />}
            {wps.isError && <ErrorState error={wps.error} onRetry={() => void wps.refetch()} />}
            {wps.isSuccess && wps.data.length === 0 && (
              <EmptyState
                title="No submission recorded"
                description="A wage-protection file leaves this system and is accepted or rejected somewhere else. That answer has to be recorded here."
              />
            )}
            {wps.isSuccess && wps.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                  <tr>
                    <th className="p-3 text-start font-medium">Entity</th>
                    <th className="p-3 text-start font-medium">Period</th>
                    <th className="p-3 text-start font-medium">File</th>
                    <th className="p-3 text-end font-medium">Records</th>
                    <th className="p-3 text-end font-medium">Amount</th>
                    <th className="p-3 text-start font-medium">Reference</th>
                    <th className="p-3 text-start font-medium">Status</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {wps.data.map((row) => (
                    <tr key={row.id} className="border-b border-[var(--color-border)]">
                      <td className="p-3">{entityName(row.legalEntityId)}</td>
                      <td className="p-3">
                        <DateValue value={row.periodStart} /> – <DateValue value={row.periodEnd} />
                      </td>
                      <td className="p-3">{row.fileName ?? '—'}</td>
                      <td className="p-3 text-end tabular-nums">{row.recordCount ?? '—'}</td>
                      <td className="p-3 text-end"><CurrencyValue value={row.totalAmount} /></td>
                      <td className="p-3">{row.reference ?? '—'}</td>
                      <td className="p-3">
                        <StatusBadge
                          status={row.status}
                          tone={STATUS_TONE[row.status] ?? 'neutral'}
                          label={row.status.toLowerCase()}
                        />
                      </td>
                      <td className="p-3 text-end">
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: row.id,
                              legalEntityId: row.legalEntityId,
                              periodStart: row.periodStart,
                              periodEnd: row.periodEnd,
                              reference: row.reference ?? '',
                              status: row.status,
                              responseNote: row.responseNote ?? '',
                              a: row.fileName ?? '',
                              b: row.recordCount?.toString() ?? '',
                              c: row.totalAmount?.toString() ?? '',
                            })
                          }}
                        >
                          Update
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'gpssa' && (
          <>
            {gpssa.isPending && <Skeleton className="m-4 h-20" />}
            {gpssa.isError && (
              <ErrorState error={gpssa.error} onRetry={() => void gpssa.refetch()} />
            )}
            {gpssa.isSuccess && gpssa.data.length === 0 && (
              <EmptyState
                title="No filing recorded"
                description="Pension contributions are filed outside this system. What was filed, and what came back, belongs here."
              />
            )}
            {gpssa.isSuccess && gpssa.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                  <tr>
                    <th className="p-3 text-start font-medium">Entity</th>
                    <th className="p-3 text-start font-medium">Period</th>
                    <th className="p-3 text-end font-medium">People</th>
                    <th className="p-3 text-end font-medium">Employer</th>
                    <th className="p-3 text-end font-medium">Employee</th>
                    <th className="p-3 text-start font-medium">Reference</th>
                    <th className="p-3 text-start font-medium">Status</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {gpssa.data.map((row) => (
                    <tr key={row.id} className="border-b border-[var(--color-border)]">
                      <td className="p-3">{entityName(row.legalEntityId)}</td>
                      <td className="p-3">
                        <DateValue value={row.periodStart} /> – <DateValue value={row.periodEnd} />
                      </td>
                      <td className="p-3 text-end tabular-nums">{row.employeeCount ?? '—'}</td>
                      <td className="p-3 text-end"><CurrencyValue value={row.employerAmount} /></td>
                      <td className="p-3 text-end"><CurrencyValue value={row.employeeAmount} /></td>
                      <td className="p-3">{row.reference ?? '—'}</td>
                      <td className="p-3">
                        <StatusBadge
                          status={row.status}
                          tone={STATUS_TONE[row.status] ?? 'neutral'}
                          label={row.status.toLowerCase()}
                        />
                      </td>
                      <td className="p-3 text-end">
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: row.id,
                              legalEntityId: row.legalEntityId,
                              periodStart: row.periodStart,
                              periodEnd: row.periodEnd,
                              reference: row.reference ?? '',
                              status: row.status,
                              responseNote: row.responseNote ?? '',
                              a: row.employeeCount?.toString() ?? '',
                              b: row.employerAmount?.toString() ?? '',
                              c: row.employeeAmount?.toString() ?? '',
                            })
                          }}
                        >
                          Update
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        title={tab === 'wps' ? 'Wage-protection submission' : 'Pension filing'}
        submitLabel="Save"
        loading={actions.saveWps.isPending || actions.saveGpssa.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              if (tab === 'wps') {
                await actions.saveWps.mutateAsync({
                  id: draft.id,
                  legalEntityId: draft.legalEntityId,
                  paymentBatchId: null,
                  periodStart: draft.periodStart,
                  periodEnd: draft.periodEnd,
                  fileName: draft.a || null,
                  recordCount: draft.b ? Number(draft.b) : null,
                  totalAmount: draft.c ? Number(draft.c) : null,
                  channel: null,
                  reference: draft.reference || null,
                  status: draft.status,
                  responseNote: draft.responseNote || null,
                })
              } else {
                await actions.saveGpssa.mutateAsync({
                  id: draft.id,
                  legalEntityId: draft.legalEntityId,
                  periodStart: draft.periodStart,
                  periodEnd: draft.periodEnd,
                  employeeCount: draft.a ? Number(draft.a) : null,
                  employerAmount: draft.b ? Number(draft.b) : null,
                  employeeAmount: draft.c ? Number(draft.c) : null,
                  totalAmount: draft.b && draft.c ? Number(draft.b) + Number(draft.c) : null,
                  reference: draft.reference || null,
                  status: draft.status,
                  responseNote: draft.responseNote || null,
                })
              }
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="filing-entity">Legal entity</Label>
              <Select
                id="filing-entity" className="w-full" value={draft.legalEntityId}
                onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
              >
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="filing-status">Status</Label>
              <Select
                id="filing-status" className="w-full" value={draft.status}
                onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              >
                {Object.keys(STATUS_TONE).map((value) => (
                  <option key={value} value={value}>{value.toLowerCase()}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="filing-from">Period from</Label>
              <DatePicker
                id="filing-from" value={draft.periodStart}
                onChange={(event) => setDraft({ ...draft, periodStart: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="filing-to">Period to</Label>
              <DatePicker
                id="filing-to" value={draft.periodEnd}
                onChange={(event) => setDraft({ ...draft, periodEnd: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="filing-a">{tab === 'wps' ? 'File name' : 'People filed for'}</Label>
              <Input
                id="filing-a" value={draft.a}
                onChange={(event) => setDraft({ ...draft, a: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="filing-b">{tab === 'wps' ? 'Records' : 'Employer amount'}</Label>
              <Input
                id="filing-b" type="number" value={draft.b}
                onChange={(event) => setDraft({ ...draft, b: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="filing-c">{tab === 'wps' ? 'Total amount' : 'Employee amount'}</Label>
              <Input
                id="filing-c" type="number" value={draft.c}
                onChange={(event) => setDraft({ ...draft, c: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="filing-reference">Reference</Label>
              <Input
                id="filing-reference" value={draft.reference}
                onChange={(event) => setDraft({ ...draft, reference: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="filing-response">What came back</Label>
              <Textarea
                id="filing-response" value={draft.responseNote}
                onChange={(event) => setDraft({ ...draft, responseNote: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Required for a rejection: without it nobody can tell, six months later, whether
                it was fixed or abandoned.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
